import React, { useState, useEffect, useRef } from "react";
import { PencilIcon, TrashIcon } from "@heroicons/react/20/solid";
import { FunnelIcon, XMarkIcon } from "@heroicons/react/24/solid";
import Select from "./Select";
import Button from "./Button";
import TableFilter from "./tableFilter";

interface Column<T> {
  key: keyof T;
  label: string;
  sortable?: boolean;
  align?: "left" | "center" | "right";
  render?: (item: T) => React.ReactNode;
  filterable?: boolean;
  filterType?: "text" | "number" | "date" | "select" | "multiselect";
  filterOptions?: { value: string; label: string }[]; // Added for select filter
}

interface HeaderButton {
  label: string;
  onClick: () => void;
  className?: string;
}

interface CustomTableProps<T> {
  data: T[];
  columns: Column<T>[];
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (value: number) => void;
  onSort?: (column: keyof T) => void;
  sortColumn?: keyof T;
  sortDirection?: "asc" | "desc";
  loading?: boolean;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  getId: (item: T) => number;
  title?: string;
  onCreateClick?: () => void;
  showHeader?: boolean;
  itemsPerPageOptions?: number[];
  headerButtons?: HeaderButton[];
  onFilter?: (filters: Record<string, string>) => void;
  initialSelectedRowIndex?: number | null;
  onRowSelectionChange?: (index: number | null) => void;
  headerExtra?: React.ReactNode;
  getRowClassName?: (item: T, index?: number) => string;
  getRowActions?: (item: T) => { showEdit?: boolean; showDelete?: boolean };
  /** 'gameList' = light theme with alternating rows like Game List page */
  variant?: 'default' | 'gameList';
  /** Hide rows-per-page selector in header */
  showRowsPerPage?: boolean;
  /** Hide pagination controls at bottom */
  showPagination?: boolean;
  /** Optional row rendered above column headers inside thead */
  leadingHeaderRow?: React.ReactNode;
}

const CustomTable = <T extends object>({
  data,
  columns,
  totalPages,
  currentPage,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  onSort,
  sortColumn,
  sortDirection,
  loading = false,
  onEdit,
  onDelete,
  getId,
  title = "Data Table",
  onCreateClick,
  showHeader = true,
  headerButtons = [],
  itemsPerPageOptions = [10, 20, 30, 50, 100],
  onFilter,
  initialSelectedRowIndex = null,
  onRowSelectionChange,
  headerExtra,
  getRowClassName,
  getRowActions,
  variant = 'default',
  showRowsPerPage = true,
  showPagination = true,
  leadingHeaderRow,
}: CustomTableProps<T>) => {
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(initialSelectedRowIndex);
  const tableRef = useRef<HTMLTableElement>(null);
  const rowRefs = useRef<Map<number, HTMLElement>>(new Map());
  const cardRefs = useRef<Map<number, HTMLElement>>(new Map());
  const lastNotifiedIndexRef = useRef<number | null | undefined>(undefined);
  const isMountedRef = useRef(true);

  // Update selected row when initialSelectedRowIndex changes
  useEffect(() => {
    if (initialSelectedRowIndex !== undefined && isMountedRef.current) {
      setSelectedRowIndex(initialSelectedRowIndex);
    }
  }, [initialSelectedRowIndex]);

  // Notify parent when selection changes (only when value actually changes to avoid update loops)
  useEffect(() => {
    if (!isMountedRef.current) return;
    if (lastNotifiedIndexRef.current === selectedRowIndex) return;
    lastNotifiedIndexRef.current = selectedRowIndex;
    onRowSelectionChange?.(selectedRowIndex);
  }, [selectedRowIndex, onRowSelectionChange]);

  // Track mounted state to prevent setState after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleApplyFilters = (filters: Record<string, string>) => {
    setFilterValues(filters);
    onFilter?.(filters);
  };

  const handleClearFilters = () => {
    const clearedFilters = Object.fromEntries(
      Object.keys(filterValues).map((key) => [key, ""])
    );
    setFilterValues(clearedFilters);
    onFilter?.(clearedFilters);
  };

  const filterableColumns = columns.filter((col) => col.filterable);

  // Prepare filter config for TableFilter component
  const filterConfigs = filterableColumns.map((column) => ({
    name: String(column.key),
    label: column.label,
    type: column.filterType || "text",
    options: column.filterOptions,
    width: "min-w-[200px]",
  }));

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isMountedRef.current) return;
      // Don't handle keyboard events when typing in inputs or textareas
      if (
        document.activeElement?.tagName === "INPUT" ||
        document.activeElement?.tagName === "TEXTAREA" ||
        (document.activeElement as HTMLElement)?.isContentEditable
      ) {
        return;
      }
      // Only handle if the table is still in the document (user hasn't navigated away)
      if (!tableRef.current || !document.contains(tableRef.current)) return;

      // Handle Arrow Up
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (selectedRowIndex === null || selectedRowIndex === 0) {
          setSelectedRowIndex(data.length > 0 ? data.length - 1 : null);
        } else {
          setSelectedRowIndex(selectedRowIndex - 1);
        }
        return;
      }

      // Handle Arrow Down
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (selectedRowIndex === null || selectedRowIndex === data.length - 1) {
          setSelectedRowIndex(data.length > 0 ? 0 : null);
        } else {
          setSelectedRowIndex(selectedRowIndex + 1);
        }
        return;
      }

      // Handle Insert key or ALT + E to edit
      if (
        (e.key === "Insert" || (e.altKey && (e.key === "e" || e.key === "E"))) &&
        selectedRowIndex !== null &&
        selectedRowIndex >= 0 &&
        selectedRowIndex < data.length &&
        onEdit
      ) {
        e.preventDefault();
        const selectedItem = data[selectedRowIndex];
        onEdit(getId(selectedItem));
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedRowIndex, data, onEdit, getId]);

  // Scroll selected row into view and focus it
  useEffect(() => {
    if (!isMountedRef.current) return;
    if (selectedRowIndex !== null && selectedRowIndex >= 0 && selectedRowIndex < data.length) {
      const tableRow = rowRefs.current.get(selectedRowIndex);
      const cardRow = cardRefs.current.get(selectedRowIndex);
      // Pick the visible element (offsetParent is null for display:none)
      const el = (tableRow && tableRow.offsetParent !== null) ? tableRow
               : (cardRow && cardRow.offsetParent !== null) ? cardRow
               : tableRow || cardRow;
      if (el && document.contains(el)) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        el.setAttribute('tabindex', '-1');
        requestAnimationFrame(() => {
          if (isMountedRef.current && document.contains(el)) {
            el.focus();
          }
        });
      }
    }
  }, [selectedRowIndex, data.length]);

  // Reset selection when data changes or page changes, but only if not controlled externally
  useEffect(() => {
    if (!isMountedRef.current) return;
    if (initialSelectedRowIndex === undefined) {
      setSelectedRowIndex(null);
    } else if (initialSelectedRowIndex === null && data.length > 0) {
      // If externally set to null but we have data, allow internal management
      setSelectedRowIndex(null);
    }
  }, [data.length, currentPage, initialSelectedRowIndex]);

  const isGameListVariant = variant === 'gameList';

  return (
    <div className={isGameListVariant ? 'w-full mx-auto' : 'p-1 w-full mx-auto'}>
      {showHeader && (
        <>
          <div className={`flex flex-wrap items-center gap-2 p-1.5 sm:p-1 rounded-sm rounded-b-none ${
            isGameListVariant ? 'bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 p-4' : 'bg-gray-100'
          }`}>
            <h2 className={`font-extrabold text-sm sm:text-base ${isGameListVariant ? 'text-slate-800' : 'text-gray-800'}`}>{title}</h2>
            {showRowsPerPage && (
            <div className="flex items-center">
              <span className="text-gray-800 font-bold text-xs sm:text-sm mr-1 sm:mr-2">Rows:</span>
              <Select
                options={itemsPerPageOptions.map((num) => ({
                  value: num,
                  label: String(num),
                }))}
                value={rowsPerPage}
                onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
              />
            </div>
            )}
            {headerExtra && (
              <div className="flex items-center gap-2 mr-2">
                {headerExtra}
              </div>
            )}
            <div className="flex items-center gap-1 sm:gap-2 ml-auto">
              {headerButtons?.map((btn, index) => (
                <Button
                  key={index}
                  className={
                    btn.className ??
                    'bg-blue-500 text-white text-xs sm:text-sm px-1.5 sm:px-2 py-0.5 sm:py-1 whitespace-nowrap rounded hover:bg-blue-700'
                  }
                  onClick={btn.onClick}
                >
                  {btn.label}
                </Button>
              ))}
              {filterableColumns.length > 0 && (
                <FunnelIcon
                  onClick={() => setShowFilters(!showFilters)}
                  className={`h-4 w-4 cursor-pointer flex-shrink-0 ${
                    showFilters ? "text-blue-500" : "text-gray-800"
                  }`}
                />
              )}
              {onCreateClick && (
                <Button
                  onClick={onCreateClick}
                  className="bg-blue-500 text-white text-xs sm:text-sm px-1.5 sm:px-2 py-0.5 sm:py-1 whitespace-nowrap"
                >
                  Create New
                </Button>
              )}
            </div>
          </div>

          {/* Integrated TableFilter component */}
          {showFilters && filterableColumns.length > 0 && (
            <TableFilter<T>
              filters={filterConfigs}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
              onToggle={() => setShowFilters(false)}
              visible={showFilters}
              className="border-t-0 rounded-t-none"
            />
          )}
        </>
      )}

      {/* ===== MOBILE CARD VIEW ===== */}
      <div className={`md:hidden rounded-t-none ${isGameListVariant ? 'border border-slate-200 border-t-0 bg-white shadow-md' : 'border rounded-md shadow-md'}`}>
        {loading ? (
          <div className="text-center p-4 text-gray-500">Loading...</div>
        ) : data.length === 0 ? (
          <div className="text-center p-4 text-gray-500">No records found.</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {data.map((item, index) => {
              const itemId = getId(item);
              const isSelected = selectedRowIndex === index;
              return (
                <div
                  key={itemId}
                  ref={(el) => {
                    if (el) cardRefs.current.set(index, el);
                    else cardRefs.current.delete(index);
                  }}
                  tabIndex={isSelected ? 0 : -1}
                  onClick={() => setSelectedRowIndex(index)}
                  className={`p-2.5 transition-colors ${
                    isSelected
                      ? isGameListVariant
                        ? "bg-blue-100 outline-none ring-2 ring-inset ring-blue-400 text-slate-900"
                        : "bg-blue-600 text-white outline-none ring-2 ring-inset ring-blue-400"
                      : isGameListVariant
                        ? "bg-white text-slate-900 active:bg-slate-50"
                        : "bg-white text-gray-900 active:bg-gray-100"
                  } ${getRowClassName?.(item, index) ?? ''}`}
                >
                  {columns.map((col) => (
                    <div key={String(col.key)} className="flex items-baseline justify-between gap-2 py-[2px]">
                      <span className={`text-[10px] font-bold uppercase tracking-wide flex-shrink-0 ${
                        isSelected ? (isGameListVariant ? 'text-blue-700' : 'text-blue-200') : (isGameListVariant ? 'text-slate-500' : 'text-gray-500')
                      }`}>
                        {col.label}
                      </span>
                      <span className={`text-xs text-right truncate ${
                        isSelected ? (isGameListVariant ? 'text-slate-900' : 'text-white') : (isGameListVariant ? 'text-slate-800' : 'text-gray-800')
                      }`}>
                        {col.render ? col.render(item) : (item[col.key] as React.ReactNode)}
                      </span>
                    </div>
                  ))}
                  {(onEdit || onDelete) && (() => {
                    const actions = getRowActions?.(item) ?? { showEdit: true, showDelete: true };
                    const showEdit = onEdit && actions.showEdit !== false;
                    const showDelete = onDelete && actions.showDelete !== false;
                    if (!showEdit && !showDelete) return null;
                    return (
                    <div className="flex justify-end gap-3 mt-1.5 pt-1.5 border-t border-gray-200/30">
                      {showEdit && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onEdit!(getId(item)); }}
                          className={`flex items-center gap-1 text-xs font-medium ${isSelected ? 'text-blue-200 active:text-white' : 'text-blue-600 active:text-blue-800'}`}
                        >
                          <PencilIcon className="w-3.5 h-3.5" /> Edit
                        </button>
                      )}
                      {showDelete && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onDelete!(getId(item)); }}
                          className={`flex items-center gap-1 text-xs font-medium ${isSelected ? 'text-red-300 active:text-red-100' : 'text-red-600 active:text-red-800'}`}
                        >
                          <TrashIcon className="w-3.5 h-3.5" /> Delete
                        </button>
                      )}
                    </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ===== DESKTOP TABLE VIEW ===== */}
      <div className={`hidden md:block overflow-x-auto rounded-t-none ${
        isGameListVariant ? 'border border-slate-200 border-t-0 bg-white shadow-md' : 'border rounded-md shadow-md'
      }`}>
        <table ref={tableRef} className={`min-w-full table-auto text-xs sm:text-sm ${isGameListVariant ? 'divide-y divide-slate-200' : ''}`}>
          <thead className={isGameListVariant ? 'bg-gradient-to-r from-slate-50 to-slate-100 text-left' : 'bg-gray-200 text-left'}>
            {leadingHeaderRow}
            <tr>
              {columns.map((col) => (
                <th
                  key={String(col.key)}
                  className={`${
                    isGameListVariant
                      ? 'px-2 py-1 text-xs font-semibold text-slate-700 uppercase tracking-wider'
                      : 'p-1.5 sm:p-2'
                  } whitespace-nowrap text-${col.align ?? "left"} ${
                    col.align === "right" ? "pr-4" : ""
                  } ${
                    col.sortable ? "cursor-pointer hover:bg-slate-100 transition-colors" : ""
                  }`}
                  onClick={() => col.sortable && onSort?.(col.key)}
                >
                  {col.label}
                  {sortColumn === col.key &&
                    (sortDirection === "asc" ? " ▲" : " ▼")}
                </th>
              ))}
              {(onEdit || onDelete) && (
                <th className={`text-right whitespace-nowrap ${isGameListVariant ? 'px-2 py-1 text-xs font-semibold text-slate-700 uppercase tracking-wider' : 'p-1.5 sm:p-2'}`}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody className={`text-sm ${isGameListVariant ? 'bg-white divide-y divide-slate-200 text-slate-900' : 'text-white'}`}>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className={`text-center p-4 ${isGameListVariant ? 'text-slate-500' : ''}`}>
                  {isGameListVariant ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
                      <p className="text-slate-500 text-sm font-medium">Loading...</p>
                    </div>
                  ) : 'Loading...'}
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className={`text-center p-4 ${isGameListVariant ? 'text-slate-500' : ''}`}>
                  {isGameListVariant ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <p className="text-slate-600 text-sm font-medium">No records found</p>
                      <p className="text-slate-400 text-xs mt-1">Try adjusting your filters</p>
                    </div>
                  ) : 'No records found.'}
                </td>
              </tr>
            ) : (
              data.map((item, index) => {
                const itemId = getId(item);
                const isSelected = selectedRowIndex === index;
                return (
                <tr
                  key={itemId}
                  ref={(el) => {
                    if (el) {
                      rowRefs.current.set(index, el);
                    } else {
                      rowRefs.current.delete(index);
                    }
                  }}
                  tabIndex={isSelected ? 0 : -1}
                  className={`${
                    isGameListVariant ? 'border-b-0' : 'border-b'
                  } ${
                    isSelected
                      ? isGameListVariant
                        ? "bg-blue-100 hover:bg-blue-100 outline-none ring-2 ring-blue-400"
                        : "bg-blue-600 hover:bg-blue-700 outline-none ring-2 ring-blue-400"
                      : isGameListVariant
                        ? `hover:bg-blue-50 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`
                        : "hover:bg-gray-500"
                  } ${getRowClassName?.(item, index) ?? ''}`}
                >
                  {columns.map((col) => (
                    <td
                      key={String(col.key)}
                      className={`text-${col.align ?? "left"} ${
                        isGameListVariant ? 'px-2 py-0.5 whitespace-nowrap leading-5' : 'p-1.5 sm:p-2'
                      }`}
                    >
                      {col.render
                        ? col.render(item)
                        : (item[col.key] as React.ReactNode)}
                    </td>
                  ))}
                  {(onEdit || onDelete) && (() => {
                    const actions = getRowActions?.(item) ?? { showEdit: true, showDelete: true };
                    const showEdit = onEdit && actions.showEdit !== false;
                    const showDelete = onDelete && actions.showDelete !== false;
                    return (
                    <td className={`text-right space-x-1 whitespace-nowrap ${isGameListVariant ? 'px-2 py-0.5 leading-5' : 'p-1.5 sm:p-2'}`}>
                      {showEdit && (
                        <PencilIcon
                          className="inline-block h-4 w-4 text-blue-600 cursor-pointer"
                          onClick={() => onEdit!(getId(item))}
                        />
                      )}
                      {showDelete && (
                        <TrashIcon
                          className="inline-block h-4 w-4 text-red-600 cursor-pointer"
                          onClick={() => onDelete!(getId(item))}
                        />
                      )}
                    </td>
                    );
                  })()}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
      <div className={`flex items-center justify-between gap-2 mt-2 sm:mt-4 ${
        isGameListVariant && totalPages > 0 ? 'bg-slate-50 px-6 py-4 border-t border-slate-200 flex-col sm:flex-row' : ''
      }`}>
        {isGameListVariant && totalPages > 0 && (
          <div className="text-sm text-slate-600">
            Showing page <span className="font-semibold text-slate-900">{currentPage + 1}</span> of{' '}
            <span className="font-semibold text-slate-900">{totalPages}</span>
          </div>
        )}
        <div className={`flex items-center gap-2 ${isGameListVariant ? 'ml-auto' : ''}`}>
          <Button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
            className={isGameListVariant
              ? `px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  currentPage === 0
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-600 border border-slate-300 shadow-sm'
                }`
              : "px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm flex-shrink-0"
            }
          >
            Previous
          </Button>
          <div className="flex flex-wrap justify-center gap-0.5 sm:gap-1 min-w-0 overflow-hidden">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                onClick={() => onPageChange(index)}
                className={isGameListVariant
                  ? `px-3 py-2 rounded-lg font-medium transition-all duration-200 min-w-[40px] ${
                      index === currentPage
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-600 border border-slate-300'
                    }`
                  : `px-2 sm:px-3 py-0.5 sm:py-1 rounded text-xs sm:text-sm ${
                      index === currentPage
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-300"
                    }`
                }
              >
                {index + 1}
              </button>
            ))}
          </div>
          <Button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
            className={isGameListVariant
              ? `px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  currentPage >= totalPages - 1
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-600 border border-slate-300 shadow-sm'
                }`
              : "px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm flex-shrink-0"
            }
          >
            Next
          </Button>
        </div>
      </div>
      )}
    </div>
  );
};

export default CustomTable;
